import { useMemo, useState, useEffect, useRef } from "react";
import { formatActivityShort, activityPillStyle } from "@/lib/dates";
import { TaskActivityHovercard } from "@/components/documents/ActivityHovercard";
import { useNavigate } from "@/hooks/useDeskNavigate";
import {
  Paperclip, MessageCircle, ListChecks, CheckSquare, FileText, Link2, Info, Pencil, Trash2, Clock,
  Repeat,
} from "lucide-react";
import {
  TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";
import ColumnHeaderMenu from "@/components/ui/ColumnHeaderMenu";
import StatusBadge, { STATUS_LABEL, STATUS_COLOR } from "./StatusBadge";
import PriorityBadge, { PRIORITY_LABEL } from "./PriorityBadge";
import PriorityQuickPicker from "./PriorityQuickPicker";
import ReadStatusBadge, { formatReactionDelay } from "./ReadStatusBadge";
import { MODULE_COLORS } from "@/lib/moduleColors";
import UserAvatar from "@/components/ui/UserAvatar";
import AvatarNamePopover from "@/components/ui/AvatarNamePopover";
import EmptyState from "@/components/ui/EmptyState";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePersistedColFilters } from "@/lib/persistedFilters";
import { toast } from "@/lib/toast";
import { Send } from "lucide-react";
import { DeadlineCellCompact } from "./DeadlineDisplay";
import RelanceBell from "./RelanceBell";

/**
 * TaskListView — enriched full-width table.
 *
 *  Header is a *two-row* structure:
 *    Row 1 → group labels (colspan) : Identification · Destination · Personnes · Contenu · Dates
 *    Row 2 → real columns           : Réf · Objet · Statut · Priorité · Siège / Projet · Créé par · Assigné(s) · 📎 💬 ☑ · Deadline · Terminée le
 *
 *  Each real column keeps its yellow ▾ menu for sort + per-column filter.
 *  Row background is tinted by effective_status (violet added for `to_verify`).
 *
 *  Spec 2026-02-XX rev7 (Feb 2026) — le menu de tri/filtre est désormais
 *  fourni par le composant partagé ``<ColumnHeaderMenu>`` (mutualisé avec
 *  Documents). Le local ``HeaderMenu`` a été supprimé.
 */
const YELLOW = MODULE_COLORS.tasks;

// Spec 2026-02-XX rev40 — La table `ROW_BG` (coloration de fond par
// statut) est retirée. Le badge Statut + la barre latérale gauche
// (boxShadow inset) restent seuls porteurs de l'info visuelle. Cohérent
// avec la refonte Documents rev17.

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", {
  day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
}) : "—");

const fmtShort = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", {
  day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit",
}).replace(",", "") : "—");

// ---------------------------------------------------------------------------
// Spec rev150 (2026-02-07) — Deadline enrichie.
// Le composant DeadlineCell + le helper deadlineMeta ont été extraits
// dans /components/tasks/DeadlineDisplay.jsx (spec rev151, 2026-02-07)
// pour partage avec TaskKanbanView. Cette section conserve uniquement
// les helpers d'affichage locaux non partagés.
// ---------------------------------------------------------------------------

/**
 * HeaderMenu — thin adapter over the shared ``<ColumnHeaderMenu>`` so the
 * existing call-sites keep working without changing every one of them.
 * The shared component drives every table's sort/filter menu identically.
 */
function HeaderMenu(props) {
  return <ColumnHeaderMenu {...props} accent={YELLOW} />;
}

/**
 * formatRelativeShort — Format court pour timestamps dans le popover
 * (aperçu de messages). Ex : "à l'instant", "3 min", "2 h", "hier",
 * "3 j", "12 sept". Différent de `formatRelative` (utilisé ailleurs)
 * pour rester compact dans le petit espace du popover.
 */
function formatRelativeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffS = Math.max(0, Math.round((now - d) / 1000));
  if (diffS < 60) return "à l'instant";
  const diffM = Math.round(diffS / 60);
  if (diffM < 60) return `${diffM} min`;
  const diffH = Math.round(diffM / 60);
  if (diffH < 24) return `${diffH} h`;
  const diffD = Math.round(diffH / 24);
  if (diffD === 1) return "hier";
  if (diffD < 7) return `${diffD} j`;
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
}

/**
 * TaskDetailsPreview — Popover d'aperçu au survol de l'icône Info (colonne
 * Actions) — spec rev52 (2026-08-02). Structure alignée sur le popover
 * `DocumentDetailsPreview` du module Documents :
 *   • Fond #0F0F10, cartes internes #1E1E1E, bordure #2A2A2A.
 *   • Liseré gauche 4px coloré par le statut effectif de la tâche
 *     (cohérent avec la barre latérale de la ligne du tableau).
 *   • En-tête : titre + référence en #F9BC26 + badge statut à droite.
 *   • Carte « Infos » 2 colonnes : Siège/Projet + Deadline.
 *   • Carte « Description » (fallback "—").
 *   • Carte « Assignés » : avatar + nom + statut de lecture individuel.
 *   • 2 cartes côte à côte : Checklist (N/M) + Pièces jointes (N).
 *   • Résumé cliquable Discussion (N messages, ouvre la fiche onglet chat).
 */
function TaskDetailsPreview({ task, projectsById, usersById }) {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id;
  const status = task.effective_status;
  const statusColor = STATUS_COLOR[status] || "#94a3b8";
  const statusLabel = STATUS_LABEL[status] || (status || "—");
  const projectName = projectsById?.[task.project_id]?.name || "—";
  const deadline = task.deadline
    ? new Date(task.deadline).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" })
    : "—";
  const description = (task.message || task.description || "").trim();
  const assignments = useMemo(() => task.assignments || [], [task.assignments]);
  const chkTotal = (task.checklist || []).length;
  const chkDone = (task.checklist || []).filter((c) => c.done).length;
  const attCount = task.attachments_count ?? 0;
  const msgCount = task.messages_count ?? 0;
  const unreadCount = task.unread_messages_count ?? 0;
  // Même règle que la fiche : Admin / Premier Admin ont toujours accès à la discussion.
  const viewerIsAdmin = currentUser?.role === "admin" || !!currentUser?.is_first_admin;
  const isSolo = !viewerIsAdmin && assignments.length > 0 && assignments.every((a) => a.user_id === task.created_by);

  // Spec rev53 (2026-08-02) — aperçu inline des 2-3 derniers messages dans
  // le popover. Fetch lazy quand le popover est monté (via HoverCard) pour
  // éviter la surcharge réseau au chargement du tableau. On limite à
  // `PREVIEW_MSG_LIMIT` messages (les plus récents en dernier).
  const PREVIEW_MSG_LIMIT = 3;
  const [previewMsgs, setPreviewMsgs] = useState(null); // null = loading, [] = empty, [...] = loaded
  useEffect(() => {
    let cancelled = false;
    // Skip fetch quand aucune discussion possible (solo) OU aucun message
    if (isSolo || msgCount === 0) {
      setPreviewMsgs([]);
      return;
    }
    (async () => {
      try {
        const r = await api.get(`/tasks/${task.id}/messages`);
        if (cancelled) return;
        const arr = Array.isArray(r.data) ? r.data : [];
        // API renvoie ordre chronologique croissant → on prend les N derniers
        setPreviewMsgs(arr.slice(-PREVIEW_MSG_LIMIT));
      } catch {
        if (!cancelled) setPreviewMsgs([]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task.id]);

  // Spec rev54 (2026-08-02) — Répondre en un clic depuis le popover.
  // Le champ n'est actif que si la discussion l'est (non-solo). L'envoi
  // POST /tasks/{id}/messages actualise `previewMsgs` en append.
  // Spec rev55 (2026-08-02) — Support des mentions @Prénom :
  //   • En tapant "@" un dropdown liste les assignés de la tâche.
  //   • Sélection insère "@Prénom " et pousse l'user_id dans `replyMentions`.
  //   • L'envoi passe `mentions: replyMentions` (dédupliqué) au backend.
  const replyInputRef = useRef(null);
  const [replyDraft, setReplyDraft] = useState("");
  const [replyMentions, setReplyMentions] = useState([]); // user_ids
  const [replyBusy, setReplyBusy] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(-1); // index du "@" dans replyDraft

  // Liste des candidats pour le picker : les assignés + le créateur, dédupliqué.
  const mentionCandidates = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const a of assignments) {
      if (seen.has(a.user_id)) continue;
      seen.add(a.user_id);
      const u = usersById?.[a.user_id];
      if (!u) continue;
      out.push(u);
    }
    if (!seen.has(task.created_by)) {
      const cu = usersById?.[task.created_by];
      if (cu) out.push(cu);
    }
    // On exclut l'utilisateur connecté (pas de sens de se mentionner soi-même).
    return out.filter((u) => u.id !== currentUserId);
  }, [assignments, usersById, task.created_by, currentUserId]);

  const filteredMentions = useMemo(() => {
    const q = (mentionQuery || "").toLowerCase().trim();
    if (!q) return mentionCandidates.slice(0, 6);
    return mentionCandidates.filter((u) => {
      const first = (u.first_name || "").toLowerCase();
      const last = (u.last_name || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      return first.startsWith(q) || last.startsWith(q) || email.startsWith(q)
             || `${first} ${last}`.includes(q);
    }).slice(0, 6);
  }, [mentionQuery, mentionCandidates]);

  const handleReplyChange = (e) => {
    const val = e.target.value;
    setReplyDraft(val);
    // Détection du token @ courant : cherche le dernier "@" avant le curseur
    // sans espace intercalé après lui.
    const caret = e.target.selectionStart ?? val.length;
    const upto = val.slice(0, caret);
    const at = upto.lastIndexOf("@");
    if (at < 0) { setMentionOpen(false); setMentionStart(-1); setMentionQuery(""); return; }
    // Doit être début de chaîne OU précédé d'un espace/newline.
    const prev = at > 0 ? upto[at - 1] : " ";
    if (prev !== " " && prev !== "\n" && at !== 0) {
      setMentionOpen(false); setMentionStart(-1); setMentionQuery(""); return;
    }
    const q = upto.slice(at + 1);
    // Le token s'arrête au premier espace : si l'utilisateur a déjà tapé un
    // espace après le @, on ferme le picker (mention validée par sélection
    // ou abandonnée).
    if (q.includes(" ")) { setMentionOpen(false); setMentionStart(-1); setMentionQuery(""); return; }
    setMentionStart(at);
    setMentionQuery(q);
    setMentionOpen(true);
  };

  const insertMention = (user) => {
    if (mentionStart < 0) return;
    const first = user.first_name || user.email || "user";
    const before = replyDraft.slice(0, mentionStart);
    const afterCaret = replyDraft.slice(mentionStart + 1 + mentionQuery.length);
    const inserted = `@${first} `;
    const next = `${before}${inserted}${afterCaret}`;
    setReplyDraft(next);
    setReplyMentions((prev) => prev.includes(user.id) ? prev : [...prev, user.id]);
    setMentionOpen(false);
    setMentionStart(-1);
    setMentionQuery("");
    // Refocus + place caret juste après la mention insérée
    setTimeout(() => {
      const inp = replyInputRef.current;
      if (inp) {
        const pos = before.length + inserted.length;
        inp.focus();
        try { inp.setSelectionRange(pos, pos); } catch { /* noop */ }
      }
    }, 0);
  };

  const sendReply = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    const body = replyDraft.trim();
    if (!body || replyBusy || isSolo) return;
    setReplyBusy(true);
    try {
      // Dédup + filtre : on ne garde que les mentions effectivement
      // présentes dans le texte final (l'utilisateur a pu supprimer le
      // "@Prénom" manuellement après insertion).
      const cleanMentions = Array.from(new Set(replyMentions)).filter((uid) => {
        const u = usersById?.[uid];
        if (!u) return false;
        const first = u.first_name || u.email || "";
        return body.includes(`@${first}`);
      });
      const r = await api.post(`/tasks/${task.id}/messages`, {
        body,
        mentions: cleanMentions,
      });
      const posted = r.data;
      setPreviewMsgs((prev) => {
        const base = Array.isArray(prev) ? prev : [];
        return [...base, posted].slice(-PREVIEW_MSG_LIMIT);
      });
      setReplyDraft("");
      setReplyMentions([]);
      toast.success(cleanMentions.length > 0
        ? `Message envoyé (${cleanMentions.length} mention${cleanMentions.length > 1 ? "s" : ""})`
        : "Message envoyé");
    } catch (err) {
      toast.error("Impossible d'envoyer le message");
    } finally {
      setReplyBusy(false);
    }
  };

  const cardStyle = { background: "#1E1E1E", border: "1px solid #2A2A2A" };
  const cardHeaderCls = "text-[10px] uppercase tracking-widest text-muted-foreground";

  const openChat = (e) => {
    e.stopPropagation();
    if (isSolo) return;
    navigate(`/taches/${task.id}?scroll=chat`);
  };

  // Spec rev55 (2026-08-02) — factorisation du mini formulaire « Répondre »
  // avec support du picker de mentions @Prénom. Utilisé 2x (msg > 0 et
  // msg === 0), avec un placeholder différent selon le cas.
  const renderReplyForm = ({ starter = false } = {}) => (
    <form
      onSubmit={sendReply}
      onClick={(e) => e.stopPropagation()}
      className="relative flex items-center gap-1.5 pt-1.5 mt-1.5 border-t"
      style={{ borderColor: "rgba(255,255,255,0.06)" }}
      data-testid={`task-preview-reply-form-${task.id}`}
    >
      <input
        ref={replyInputRef}
        type="text"
        value={replyDraft}
        onChange={handleReplyChange}
        onKeyDown={(e) => {
          if (e.key === "Escape") {
            e.stopPropagation();
            setMentionOpen(false);
          } else if (mentionOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
            // Prevent caret jump inside input (arrows navigate picker items only).
            e.preventDefault();
          } else if (mentionOpen && e.key === "Enter" && filteredMentions[0]) {
            // Enter tout en picker ouvert → insère la 1re suggestion.
            e.preventDefault();
            e.stopPropagation();
            insertMention(filteredMentions[0]);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        placeholder={starter ? "Lancer la discussion (tape @ pour mentionner)…" : "Répondre… (tape @ pour mentionner)"}
        disabled={replyBusy}
        className="flex-1 px-2 py-1 text-[10.5px] text-white rounded outline-none focus:ring-1"
        style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.08)" }}
        data-testid={`task-preview-reply-input-${task.id}`}
      />
      <button
        type="submit"
        disabled={!replyDraft.trim() || replyBusy}
        onClick={(e) => e.stopPropagation()}
        className="inline-flex items-center justify-center rounded-full transition-transform hover:scale-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
        style={{ width: 24, height: 24, background: "#25D366", color: "#1a1a1a", border: "1px solid rgba(37,211,102,0.85)" }}
        title="Envoyer"
        data-testid={`task-preview-reply-send-${task.id}`}
      >
        <Send size={11} strokeWidth={2.6} />
      </button>
      {/* Dropdown mentions @ — positionné au-dessus du champ */}
      {mentionOpen && filteredMentions.length > 0 && (
        <div
          className="absolute left-0 right-8 bottom-full mb-1 rounded-md shadow-2xl overflow-hidden z-50"
          style={{ background: "#141414", border: "1px solid #2A2A2A" }}
          onClick={(e) => e.stopPropagation()}
          data-testid={`task-preview-reply-mentions-${task.id}`}
        >
          <div className="px-2 py-1 text-[8.5px] uppercase tracking-widest text-muted-foreground border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            Mentionner un assigné
          </div>
          <ul className="max-h-40 overflow-y-auto">
            {filteredMentions.map((u, idx) => {
              const fullName = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); insertMention(u); }}
                    className="w-full flex items-center gap-2 px-2 py-1.5 text-[10.5px] text-left hover:bg-white/[0.05] transition-colors"
                    style={idx === 0 ? { background: "rgba(249,188,38,0.06)" } : undefined}
                    data-testid={`task-preview-reply-mention-item-${task.id}-${u.id}`}
                  >
                    <UserAvatar user={u} size={18} showPopover={false} className="shrink-0" />
                    <span className="text-white font-medium flex-1 truncate">{fullName}</span>
                    <span className="text-muted-foreground text-[9px]">@{u.first_name || u.email}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </form>
  );

  return (
    <div
      className="rounded-lg"
      style={{
        background: "#0F0F10",
        padding: 16,
        // Spec rev52 — liseré gauche coloré par le statut (miroir de la ligne).
        boxShadow: `inset 4px 0 0 0 ${statusColor}`,
        // Spec rev73 (2026-08-02) — popover agrandi et taille figée à 460 px
        // pour coller au style du popover Info Documents (même largeur,
        // même respiration interne). Le HoverCardContent porte aussi
        // `width: 460` pour éviter tout écart.
        width: 460,
      }}
      data-testid={`task-preview-${task.id}`}
    >
      <div className="space-y-3">
        {/* ── EN-TÊTE ─────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-white truncate" data-testid={`task-preview-title-${task.id}`}>
              {task.subject || "—"}{task.indice && <><span className="font-normal" style={{ color: "#a4a4ae" }}> : </span><span style={{ color: "#f4c877", fontWeight: 500 }}>{task.indice}</span></>}
            </div>
            <div
              className="mt-0.5 font-bold tabular-nums"
              style={{
                fontFamily: '"SF Mono","Consolas","Menlo",monospace',
                fontSize: 11,
                color: "#F9BC26",
                letterSpacing: "0.01em",
              }}
              data-testid={`task-preview-ref-${task.id}`}
            >
              {task.reference || "—"}
            </div>
          </div>
          <span
            className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full whitespace-nowrap shrink-0"
            style={{ background: `${statusColor}22`, color: statusColor, border: `1px solid ${statusColor}55` }}
            data-testid={`task-preview-status-${task.id}`}
          >
            {statusLabel}
          </span>
        </div>

        {/* ── CARTE INFOS (Siège/Projet + Deadline) ───────────────────── */}
        <div className="rounded-lg p-3" style={cardStyle}>
          <div className={cardHeaderCls + " mb-2"}>Infos</div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
            <div className="min-w-0">
              <div className="text-muted-foreground">Siège / Projet</div>
              <div className="text-white font-medium truncate" title={projectName} data-testid={`task-preview-project-${task.id}`}>{projectName}</div>
            </div>
            <div className="min-w-0">
              <div className="text-muted-foreground">Deadline</div>
              <div className="text-white font-medium">{deadline}</div>
            </div>
          </div>
        </div>

        {/* ── CARTE DESCRIPTION ───────────────────────────────────────── */}
        <div className="rounded-lg p-3" style={cardStyle}>
          <div className={cardHeaderCls + " mb-2"}>Description</div>
          <div
            className="text-[11px] text-white leading-relaxed whitespace-pre-wrap break-words max-h-24 overflow-y-auto pr-1"
            data-testid={`task-preview-desc-${task.id}`}
          >
            {description || <span className="text-muted-foreground/60">—</span>}
          </div>
        </div>

        {/* ── CARTE ASSIGNÉS ──────────────────────────────────────────── */}
        {assignments.length > 0 && (
          <div className="rounded-lg p-3" style={cardStyle}>
            <div className="flex items-center justify-between mb-2">
              <div className={cardHeaderCls}>Assignés</div>
              <div className="text-[11px] tabular-nums text-white font-medium">
                {assignments.filter((a) => a.first_read_at).length}/{assignments.length} vus
              </div>
            </div>
            <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {assignments.map((a) => {
                const u = usersById?.[a.user_id];
                const fullName = u
                  ? `${u.first_name || ""} ${u.last_name || ""}`.trim()
                  : (a.user_id?.slice(0, 8) || "—");
                const seen = !!a.first_read_at;
                const delay = seen ? formatReactionDelay(a.reaction_delay_seconds) : null;
                const seenColor = seen ? "#22C55E" : "#6B7280";
                return (
                  <li
                    key={a.id}
                    className="flex items-center gap-2 text-[11px]"
                    data-testid={`task-preview-assignee-${task.id}-${a.user_id}`}
                  >
                    <UserAvatar user={u || { id: a.user_id, first_name: "?", last_name: "" }} size={22} showPopover={false} className="shrink-0" />
                    <span className="text-white font-medium flex-1 truncate">{fullName}</span>
                    <span style={{ color: seenColor }} className="whitespace-nowrap text-[10px]">
                      {seen ? (delay ? `Vu · ${delay}` : "Vu") : "Non vu"}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* ── 2 CARTES CÔTE À CÔTE : Checklist + Pièces jointes ───────── */}
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg p-3" style={cardStyle}>
            <div className={cardHeaderCls + " mb-2"}>Checklist</div>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center rounded-full shrink-0"
                style={{
                  width: 26, height: 26,
                  background: chkTotal > 0 ? "#F9BC26" : "rgba(107,114,128,0.10)",
                  color: chkTotal > 0 ? "#1a1a1a" : "#6B7280",
                  border: "1px solid " + (chkTotal > 0 ? "rgba(249,188,38,0.85)" : "rgba(107,114,128,0.25)"),
                }}
              >
                <ListChecks size={13} strokeWidth={2.6} />
              </span>
              <div className="text-[11px] leading-tight">
                {chkTotal > 0 ? (
                  <>
                    <div className="text-white font-medium tabular-nums">{chkDone}/{chkTotal}</div>
                    <div className="text-muted-foreground">terminée{chkDone > 1 ? "s" : ""}</div>
                  </>
                ) : (
                  <div className="text-muted-foreground/60">Aucune</div>
                )}
              </div>
            </div>
          </div>
          <div className="rounded-lg p-3" style={cardStyle}>
            <div className={cardHeaderCls + " mb-2"}>Pièces jointes</div>
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center justify-center rounded-full shrink-0"
                style={{
                  width: 26, height: 26,
                  background: attCount > 0 ? "#F9BC26" : "rgba(107,114,128,0.10)",
                  color: attCount > 0 ? "#1a1a1a" : "#6B7280",
                  border: "1px solid " + (attCount > 0 ? "rgba(249,188,38,0.85)" : "rgba(107,114,128,0.25)"),
                }}
              >
                <Paperclip size={13} strokeWidth={2.6} />
              </span>
              <div className="text-[11px] leading-tight">
                {attCount > 0 ? (
                  <>
                    <div className="text-white font-medium tabular-nums">{attCount}</div>
                    <div className="text-muted-foreground">fichier{attCount > 1 ? "s" : ""}</div>
                  </>
                ) : (
                  <div className="text-muted-foreground/60">Aucune</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── DISCUSSION (aperçu inline des derniers messages) ────────── */}
        {/* Spec rev53 (2026-08-02) — remplacement du simple compteur par
            un aperçu des 2-3 derniers messages (auteur + extrait court +
            date relative), pour capter en un coup d'œil ce qui vient de
            bouger sans ouvrir la fiche.
            Spec rev54 (2026-08-02) — la carte n'est plus un <button>
            monolithique (invalide car contient un formulaire) : elle est
            un <div> et seul l'en-tête (icône + label + compteur) est
            cliquable pour ouvrir la discussion complète. */}
        <div
          className="w-full rounded-lg p-3 transition-colors"
          style={{
            ...cardStyle,
            opacity: isSolo ? 0.6 : 1,
          }}
          data-testid={`task-preview-chat-${task.id}`}
        >
          <div
            role={isSolo ? undefined : "button"}
            tabIndex={isSolo ? -1 : 0}
            onClick={openChat}
            onKeyDown={(e) => { if (!isSolo && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openChat(e); } }}
            className={"flex items-center gap-2 mb-2 " + (isSolo ? "" : "cursor-pointer hover:brightness-125")}
            title={isSolo ? "Tâche solo — discussion indisponible" : "Ouvrir la discussion complète"}
            data-testid={`task-preview-chat-open-${task.id}`}
          >
            <span
              className="inline-flex items-center justify-center rounded-full shrink-0"
              style={{
                width: 26, height: 26,
                background: isSolo ? "rgba(107,114,128,0.10)" : "#25D366",
                color: isSolo ? "#6B7280" : "#1a1a1a",
                border: `1px solid ${isSolo ? "rgba(107,114,128,0.25)" : "rgba(37,211,102,0.85)"}`,
              }}
            >
              <MessageCircle size={13} strokeWidth={2.6} />
            </span>
            <div className="flex-1 min-w-0">
              <div className={cardHeaderCls}>Discussion</div>
              <div className="text-[11px] text-white font-medium">
                {isSolo
                  ? "Tâche solo — indisponible"
                  : msgCount > 0
                    ? `${msgCount} message${msgCount > 1 ? "s" : ""}${unreadCount > 0 ? ` · ${unreadCount} non lu${unreadCount > 1 ? "s" : ""}` : ""}`
                    : "Aucun message"}
              </div>
            </div>
            {!isSolo && unreadCount > 0 && (
              <span
                className="text-[9px] font-bold px-1.5 py-0.5 rounded-full tabular-nums"
                style={{ background: "#EF4444", color: "#fff" }}
              >
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </div>
          {/* Aperçu inline (loading / 2-3 derniers messages) */}
          {!isSolo && msgCount > 0 && (
            <div className="space-y-1 pt-1.5 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
              {previewMsgs === null ? (
                <div className="text-[10px] text-muted-foreground italic pl-1">Chargement…</div>
              ) : previewMsgs.length === 0 ? (
                <div className="text-[10px] text-muted-foreground italic pl-1">Impossible de charger</div>
              ) : (
                previewMsgs.map((m) => {
                  const u = usersById?.[m.author_id];
                  const authorName = u
                    ? (u.first_name || u.last_name || u.email || "—")
                    : "—";
                  const excerpt = (m.body || "").replace(/\s+/g, " ").trim();
                  const truncated = excerpt.length > 70 ? excerpt.slice(0, 70) + "…" : excerpt;
                  // Spec rev54 (2026-08-02) — mise en évidence quand l'utilisateur
                  // connecté est mentionné : fond jaune très léger, liseré gauche
                  // jaune, et badge « @ vous » à côté du nom.
                  const mentionsMe = Array.isArray(m.mentions) && currentUserId && m.mentions.includes(currentUserId);
                  return (
                    <div
                      key={m.id}
                      className="flex items-start gap-1.5 text-[10.5px] rounded"
                      style={mentionsMe ? {
                        background: "rgba(249,188,38,0.08)",
                        boxShadow: "inset 3px 0 0 0 #F9BC26",
                        padding: "3px 6px 3px 8px",
                      } : { padding: "2px 0" }}
                      data-testid={`task-preview-msg-${task.id}-${m.id}`}
                    >
                      <span className="inline-flex shrink-0" style={{ marginTop: 1 }}>
                        <UserAvatar user={u || { id: m.author_id, first_name: "?", last_name: "" }} size={18} showPopover={false} />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline gap-1.5 flex-wrap">
                          <span className="text-white font-medium truncate">{authorName}</span>
                          {mentionsMe && (
                            <span
                              className="text-[8.5px] font-bold px-1 py-[1px] rounded leading-none uppercase tracking-wide"
                              style={{ background: "#F9BC26", color: "#1a1a1a" }}
                              data-testid={`task-preview-msg-mention-${task.id}-${m.id}`}
                            >
                              @ vous
                            </span>
                          )}
                          <span className="text-muted-foreground text-[9px] whitespace-nowrap">
                            {formatRelativeShort(m.created_at)}
                          </span>
                        </div>
                        <div className="text-muted-foreground truncate">{truncated || <span className="italic">—</span>}</div>
                      </div>
                    </div>
                  );
                })
              )}
              {/* Spec rev54 (2026-08-02) — mini champ Répondre… + bouton envoi.
                  Spec rev55 (2026-08-02) — support des mentions @Prénom via
                  dropdown positionné en absolute. */}
              {!isSolo && renderReplyForm()}
            </div>
          )}
          {/* Cas msgCount === 0 : afficher aussi le mini champ « Répondre »
              pour permettre de lancer la conversation. */}
          {!isSolo && msgCount === 0 && renderReplyForm({ starter: true })}
        </div>
      </div>
    </div>
  );
}


// Grouped-header definition — one entry per real column, tagged with the group
// it belongs to. Rendering builds the two header rows from this single source
// of truth so groups stay in sync with their columns.
//
// Spec 2026-02-XX (Feb 2026) — fusion massive de la partie droite du tableau
// pour supprimer TOUT scroll horizontal :
//   • « Clôturée le » disparaît → date de clôture affichée à l'intérieur de
//     la cellule Deadline (ligne verte en dessous, uniquement si t.closed_at).
//   • « Pièces / Description / Checklist / Contenu(legacy) / Discussion » →
//     UNE seule colonne « Contenu » qui empile horizontalement 4 mini-widgets :
//        📎 N     · pièces jointes (compteur, grisé si 0)
//        ☑ n/N   · checklist (grisé si 0/0)
//        🔁 🔔N  · récurrence + rappels custom (visibles UNIQUEMENT si actifs)
//        💬 N•   · discussion (compteur + puce rouge si non lus, clic dédié)
//   • L'icône "Description" est supprimée : peu utile en liste et déjà
//     couverte par le tooltip natif sur la ligne + accessible en 1 clic
//     depuis la fiche.
// Gain net : 5 colonnes → 1 seule. Le tableau tient désormais dans 1920px.
// Spec 2026-02-XX rev42 — GROUPS, groupSpans, isLastOfGroup supprimés
// avec le retrait de la bande d'en-tête de groupe. Les colonnes conservent
// leur tag `group` interne dans HEADERS pour un usage futur (tri par
// famille, thème, etc.), mais ne rendent plus de UI.

export default function TaskListView({ items, total, hasMore, loadingMore, onLoadMore, projects, users, sortKey, setSortKey, sortDir, setSortDir, onCreateEmpty = null, onPriorityChanged = null }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Spec rev121 (2026-02) — persistance des filtres de colonnes par
  // utilisateur (chaque compte garde ses propres filtres actifs).
  const [colFilters, setColFilters] = usePersistedColFilters("tasks", user?.id);

  const projectsById = useMemo(() => Object.fromEntries(projects.map((p) => [p.id, p])), [projects]);
  const usersById = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);
  // iter426 pt 2 — pastille « À vérifier » : créateur seul (verifierUser), plus de pile d'admins.

  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [projects]
  );
  const authorOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const t of items) {
      const u = usersById[t.created_by];
      if (u && !seen.has(u.id)) { seen.add(u.id); opts.push({ value: u.id, label: `${u.first_name} ${u.last_name}` }); }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [items, usersById]);

  // Spec rev71 (2026-08-02) — options du filtre "Assigné(s)". Multi-sélect
  // basé sur l'union des `assignments.user_id` de toutes les tâches visibles.
  const assigneeOptions = useMemo(() => {
    const seen = new Set();
    const opts = [];
    for (const t of items) {
      for (const a of (t.assignments || [])) {
        const u = usersById[a.user_id];
        if (u && !seen.has(u.id)) {
          seen.add(u.id);
          opts.push({ value: u.id, label: `${u.first_name} ${u.last_name}` });
        }
      }
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [items, usersById]);

  // Spec iter122 (Feb 2026) — 7 statuts métier alignés sur StatusBadge :
  // `accepted` porte désormais le label « Acceptée » (plus de doublon
  // avec `waiting` = En attente), et `completed` remplacé par `closed`
  // (label « Clôturée ») pour éviter la confusion « Terminée / Clôturée ».
  const statusOptions = ["waiting", "accepted", "in_progress", "late", "relance", "to_verify", "refused", "closed"]
    .map((s) => ({ value: s, label: STATUS_LABEL[s], color: STATUS_COLOR[s] }));
  // Spec iter131 (Feb 2026) — 3 niveaux visibles ("Faible/normal" retiré).
  const priorityOptions = ["moyen", "urgent", "immediat"]
    .map((p) => ({ value: p, label: PRIORITY_LABEL[p] }));

  const rows = useMemo(() => {
    let arr = items;
    if (colFilters.status)      arr = arr.filter((t) => (colFilters.status === "relance" ? t.relance_active : t.effective_status === colFilters.status));
    if (colFilters.priority)    arr = arr.filter((t) => t.priority === colFilters.priority);
    if (colFilters.project)     arr = arr.filter((t) => t.project_id === colFilters.project);
    if (Array.isArray(colFilters.author) && colFilters.author.length > 0) {
      const wantedA = new Set(colFilters.author);
      arr = arr.filter((t) => wantedA.has(t.created_by));
    } else if (typeof colFilters.author === "string" && colFilters.author) {
      arr = arr.filter((t) => t.created_by === colFilters.author);
    }
    // Spec rev71 — filtre multi-sélect « Assigné(s) ». Array de user_ids.
    if (Array.isArray(colFilters.assignees) && colFilters.assignees.length > 0) {
      const wanted = new Set(colFilters.assignees);
      arr = arr.filter((t) =>
        (t.assignments || []).some((a) => wanted.has(a.user_id))
      );
    }
    // Spec rev100 (2026-02) — filtre multi-sélect « Lecture ».
    //   seen   = tous les exécuteurs ont lu (readCount === totalExecs, > 0)
    //   unseen = au moins un exécuteur n'a pas lu (readCount < totalExecs, > 0)
    // Les tâches sans exécuteur sont exclues des deux filtres (état "none").
    if (Array.isArray(colFilters.read) && colFilters.read.length > 0) {
      const wantSeen = colFilters.read.includes("seen");
      const wantUnseen = colFilters.read.includes("unseen");
      arr = arr.filter((t) => {
        const execs = t.assignments || [];
        const total = execs.length;
        if (total === 0) return false;
        const readCount = execs.filter((a) => a.first_read_at).length;
        const isSeen = readCount === total;
        if (isSeen && wantSeen) return true;
        if (!isSeen && wantUnseen) return true;
        return false;
      });
    }

    // Spec rev102 (2026-02) — filtres binaires sur colonnes contenu/planif.
    // ─ Assigné(s) « état » (bloc secondaire, coexiste avec le multi-users).
    if (Array.isArray(colFilters.assignees_state) && colFilters.assignees_state.length > 0) {
      const sel = new Set(colFilters.assignees_state);
      arr = arr.filter((t) => {
        const has = (t.assignments || []).length > 0;
        if (has && sel.has("assigned")) return true;
        if (!has && sel.has("unassigned")) return true;
        return false;
      });
    }
    // ─ Deadline (avec/sans date définie).
    if (Array.isArray(colFilters.deadline_state) && colFilters.deadline_state.length > 0) {
      const sel = new Set(colFilters.deadline_state);
      arr = arr.filter((t) => {
        const has = !!t.deadline;
        if (has && sel.has("with")) return true;
        if (!has && sel.has("without")) return true;
        return false;
      });
    }
    // ─ Pièces jointes (attachments_count > 0).
    if (Array.isArray(colFilters.attachments) && colFilters.attachments.length > 0) {
      const sel = new Set(colFilters.attachments);
      arr = arr.filter((t) => {
        const has = (t.attachments_count ?? 0) > 0;
        if (has && sel.has("with")) return true;
        if (!has && sel.has("without")) return true;
        return false;
      });
    }
    // ─ Checklist (au moins un item défini).
    if (Array.isArray(colFilters.checklist) && colFilters.checklist.length > 0) {
      const sel = new Set(colFilters.checklist);
      arr = arr.filter((t) => {
        const has = (t.checklist || []).length > 0;
        if (has && sel.has("with")) return true;
        if (!has && sel.has("without")) return true;
        return false;
      });
    }
    // ─ Discussion :
    //   unread = il y a au moins un message ET des non-lus (unread_messages_count > 0)
    //   read   = il y a des messages MAIS tous lus (unread_messages_count === 0)
    //   none   = aucun message dans la discussion (messages_count === 0)
    if (Array.isArray(colFilters.discussion) && colFilters.discussion.length > 0) {
      const sel = new Set(colFilters.discussion);
      arr = arr.filter((t) => {
        const total = t.messages_count ?? 0;
        const unreadN = t.unread_messages_count ?? 0;
        if (total === 0) return sel.has("none");
        if (unreadN > 0) return sel.has("unread");
        return sel.has("read");
      });
    }
    arr = [...arr];
    const dir = sortDir === "asc" ? 1 : -1;
    // Spec rev54 (2026-08-02) — sortKey `recent_activity` = tri par
    // `updated_at` DESC. Backend bump `task.updated_at` sur post_message
    // (rev54), sur toggle checklist, sur assignations, edits. C'est donc
    // un proxy fidèle de « quel élément vient de bouger ».
    if (sortKey === "recent_activity") {
      arr.sort((a, b) => {
        const va = a.updated_at || a.created_at || "";
        const vb = b.updated_at || b.created_at || "";
        // Toujours DESC (plus récent en tête) — sortDir ignoré volontairement.
        return String(vb).localeCompare(String(va));
      });
      arr.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
      return arr;
    }
    arr.sort((a, b) => {
      let va, vb;
      switch (sortKey) {
        case "reference":  va = a.reference || "";                              vb = b.reference || ""; break;
        case "subject":    va = a.subject || "";                                vb = b.subject || ""; break;
        case "status":     va = a.effective_status;                             vb = b.effective_status; break;
        case "priority":   va = a.priority;                                     vb = b.priority; break;
        case "project":    va = projectsById[a.project_id]?.name || "";         vb = projectsById[b.project_id]?.name || ""; break;
        case "author":     va = (usersById[a.created_by]?.first_name || "");    vb = (usersById[b.created_by]?.first_name || ""); break;
        case "deadline":   va = a.deadline || "";                               vb = b.deadline || ""; break;
        case "completed_at": va = a.completed_at || "";                         vb = b.completed_at || ""; break;
        default:           va = a[sortKey] || "";                               vb = b[sortKey] || "";
      }
      return String(va).localeCompare(String(vb)) * dir;
    });
    // iter402 pt 1e — tâches relancées épinglées en tête chez l'assigné (flag serveur `pinned`), quel que soit le tri.
    arr.sort((a, b) => (b.pinned ? 1 : 0) - (a.pinned ? 1 : 0));
    return arr;
  }, [items, colFilters, sortKey, sortDir, projectsById, usersById]);

  const doSort = (key, dir) => { setSortKey(key); setSortDir(dir); };
  const doFilter = (key, value) => setColFilters((prev) => ({ ...prev, [key]: value }));

  // Spec 2026-02-XX rev10 — infinite scroll (remplace la Pagination
  // classique). Le parent gère fetch page N/N+1, on lui signale simplement
  // que le bas de la liste est en vue via un IntersectionObserver.
  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el || !hasMore) return undefined;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) onLoadMore?.(); },
      { rootMargin: "200px" },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, loadingMore, onLoadMore]);

  // Real columns — grouped for the double-row header structure. Each row
  // gains its testId and knows whether it's the LAST column of its group so
  // we can draw the vertical group separator.
  //
  // Spec 2026-02-XX rev2 (Feb 2026) — refonte responsive complète :
  //   • Statut + Priorité fusionnés en 1 seule colonne "Statut · Prio"
  //     (pills empilés verticalement — gain ~90px).
  //   • Colonne "Créé par" → avatar circle (initiales + tooltip) — gain ~65px.
  //   • Colonne "Assigné(s)" → pile d'avatars chevauchants + "+N" (gain ~100px).
  //   • Colonne "Exécution" renommée "Début" (plus explicite) + tooltip.
  //   • Titre : truncate cohérent, max-width réduit à 240px.
  //   • Largeurs en % via <colgroup> → responsive de 1200px à 1920px sans
  //     aucun scroll horizontal, s'étire pour remplir l'écran.
  const HEADERS = [
    { group: "identification", key: "reference",  label: "Réf.",           options: null,           filterKey: null,       testId: "reference",  width: "8.5%"  }, // iter402 pt 1i — +1,5 % pris sur Checklist
    { group: "identification", key: "subject",    label: "Objet",          options: null,           filterKey: null,       testId: "subject",    width: "18%" },
    { group: "identification", key: "status",     label: "Statut",         options: statusOptions,  filterKey: "status",   testId: "status",     width: "9%"  },
    // Spec 2026-02-XX rev6 (Feb 2026) — Statut et Priorité RÉSÉPARÉS en 2 cols
    // (annule la fusion rev2). Discussion sort de Contenu vers sa col dédiée.
    { group: "identification", key: "priority",   label: "Prio",           options: priorityOptions, filterKey: "priority", testId: "priority",  width: "6%"  },
    { group: "destination",    key: "project",    label: "Siège / Projet", options: projectOptions, filterKey: "project",  testId: "project",    width: "5%"  },
    { group: "personnes",      key: "author",     label: "Créé",           options: authorOptions,  filterKey: "author",   testId: "author",     width: "4%", multi: true },
    { group: "personnes",      key: "assignees",  label: "Assigné(s)",     options: assigneeOptions, filterKey: "assignees", testId: "assignees",  width: "8%", multi: true, secondaryLabel: "Filtrer (multi)", secondaryOptions: [{ value: "assigned", label: "Assignée" }, { value: "unassigned", label: "Non assignée" }], secondaryFilterKey: "assignees_state" },
    // Spec 2026-02-XX rev38 (Point 5) — Colonne Lecture compactée : icône
    // Eye / EyeOff uniquement + tooltip au survol pour le détail.
    // Spec rev100 (2026-02) — filtre multi-sélect Vu/Non vu ajouté.
    { group: "personnes",      key: "read",       label: "Lecture",        options: [{ value: "seen", label: "Vu" }, { value: "unseen", label: "Non vu" }], filterKey: "read", testId: "read", width: "5%", multi: true  },
    // Spec rev102 (2026-02) — filtre multi-sélect Avec/Sans date sur Deadline.
    { group: "planification",  key: "deadline",   label: "Deadline",       options: [{ value: "with", label: "Avec date définie", color: "#22C55E" }, { value: "without", label: "Sans date définie", color: "#9CA3AF" }], filterKey: "deadline_state", testId: "deadline", width: "7%", multi: true, tooltip: "Date limite — clôturée en vert dessous si applicable" },
    // Spec 2026-02-XX rev38 (Point 6) — Séparation de « Contenu » en 2 colonnes
    // distinctes : Pièces jointes et Checklist. Récurrence + rappels vivent
    // désormais dans la col Checklist (métadonnées de gestion).
    // Spec rev102 (2026-02) — filtre multi-sélect Avec/Sans sur Pièces jointes.
    { group: "contenu",        key: "attachments", label: "Pièces jointes", options: [{ value: "with", label: "Avec pièce jointe", color: "#22C55E" }, { value: "without", label: "Sans pièce jointe", color: "#9CA3AF" }], filterKey: "attachments", testId: "attachments", width: "6%", multi: true },
    // Spec rev102 (2026-02) — filtre multi-sélect Avec/Sans sur Checklist.
    { group: "contenu",        key: "checklist",   label: "Checklist",      options: [{ value: "with", label: "Avec éléments de checklist", color: "#22C55E" }, { value: "without", label: "Sans élément de checklist", color: "#9CA3AF" }], filterKey: "checklist", testId: "checklist", width: "5.5%", multi: true },
    // Spec 2026-02-XX rev6 — Discussion en col DÉDIÉE (bulle SVG + badge non-lus overlay).
    // Spec rev102 (2026-02) — filtre multi-sélect Avec non lu / Avec lu / Sans.
    { group: "discussion",     key: "discussion", label: "💬",             options: [{ value: "unread", label: "Avec message non lu", color: "#EF4444" }, { value: "read", label: "Avec message lu", color: "#22C55E" }, { value: "none", label: "Sans discussion", color: "#9CA3AF" }], filterKey: "discussion", testId: "discussion", width: "3%", multi: true },
    // Spec 2026-02-XX rev48 — colonne Actions harmonisée avec Documents.
    // 3 avatars ronds 22 px : Info bleu translucide, Édition gris sombre,
    // Suppression rouge translucide. Couleurs sémantiques universelles
    // (PAS le jaune brand Tâches).
    { group: "actions",        key: "actions",    label: "Actions",        options: null,           filterKey: null,       testId: "actions",    width: "6%"  },
  ];

  // Spec rev55 (2026-08-02) — colonne « Activité » DISCRÈTE, visible
  // uniquement quand le tri Activité récente est actif. Insérée entre
  // Discussion et Actions. Texte gris italique, timestamp relatif court.
  // La largeur de la colonne Objet est légèrement réduite pour absorber
  // l'espace occupé (18% → 15%) — évitant un débordement horizontal.
  const HEADERS_WITH_ACTIVITY = sortKey === "recent_activity"
    ? (() => {
        const out = HEADERS.map((h) => h.key === "subject" ? { ...h, width: "15%" } : h);
        const insertAt = out.findIndex((h) => h.key === "actions");
        const activityCol = {
          group: "activite", key: "activity_at",
          label: "Activité", options: null, filterKey: null,
          testId: "activity", width: "6%",
        };
        out.splice(insertAt, 0, activityCol);
        return out;
      })()
    : HEADERS;

  // Spec 2026-02-XX rev42 — groupSpans / isLastOfGroup obsolètes (voir ci-dessus).

  return (
    <div className="flex flex-col flex-1 min-h-0 gap-2" data-testid="task-list-view">
      {/* Spec rev92 (2026-08-02) — La barre de tri secondaire « Activité
          récente » a été retirée d'ici et remontée dans <Tasks> pour être
          fusionnée avec « Afficher les clôturées » (parité avec Documents
          rev90). Le sortKey/sortDir est désormais reçu en props. */}

      {/* Vertical-scroll container : header stays sticky, only rows scroll.
          - `flex-1 min-h-0` lets the table stretch to fill the remaining
            viewport height (below filters + tabs), aligning with the
            Documents module pattern (spec 2026-02-13 §UI).
          - `overflow-x-hidden` disables horizontal scroll (columns keep
            their responsive width instead of forcing a min-width). */}
      <div
        className="rounded-md border border-vertec-borderDark w-full flex-1 min-h-0 tasks-compact-table"
        style={{
          overflowY: "auto",
          overflowX: "hidden",
          background: "#1E1E1E",
        }}
        data-testid="task-list-scroll"
      >
        {/*
          Spec 2026-02-XX rev38 (Point 1) — Densité de tableau légèrement
          resserrée : font-size 12px sur les cellules et padding-y réduit
          à 6px (vs 8px par défaut de shadcn Table). Reste moins dense que
          Documents mais gagne ~20 % de lignes visibles à l'écran.
          Portée : classe `.tasks-compact-table` scopée à ce tableau seul.
        */}
        <style>{`
          .tasks-compact-table td { font-size: 12px !important; padding: 6px 8px !important; }
          .tasks-compact-table th { font-size: 11px !important; }
        `}</style>
        {/* Spec 2026-02-XX rev2 — table-layout:fixed + <colgroup> % :
            garantit une répartition proportionnelle des colonnes qui s'étire
            de 1200px à 1920px sans scroll horizontal. Cf. mockup validé. */}
        <table className="w-full caption-bottom text-sm" style={{ tableLayout: "fixed" }}>
          <colgroup>
            {HEADERS_WITH_ACTIVITY.map((h) => (
              <col key={h.key} className={`task-col-${h.key}`} style={{ width: h.width }} />
            ))}
          </colgroup>
          <TableHeader>
            {/* Spec 2026-02-XX rev42 (Point 2) — Ligne d'en-tête de GROUPE
                (Identification / Personnes / …) supprimée : elle
                n'apportait aucune info opérationnelle et prenait 33 px
                d'espace vertical. Seule la ligne d'en-tête de colonnes
                subsiste, sticky top:0. */}
            <TableRow className="hover:bg-transparent">
              {HEADERS_WITH_ACTIVITY.map((h) => (
                <TableHead
                  key={h.key}
                  className="select-none"
                  title={h.tooltip}
                  style={{
                    position: "sticky",
                    top: 0,
                    // Spec 2026-02-XX rev43 (bug scroll) — z-index élevé
                    // (30) pour rester STRICTEMENT au-dessus des avatars
                    // empilés des cellules (Assigné : chaque avatar reçoit
                    // zIndex 3-ai). Sans ça, l'avatar HB d'une ligne qui
                    // scrolle sous le header apparaissait par-dessus la
                    // cellule d'en-tête. Cohérent avec Documents.
                    zIndex: 30,
                    background: "#262626",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  <HeaderMenu
                    label={h.label}
                    options={h.options}
                    // Spec rev71 — quand `multi=true`, `current` doit être un
                    // tableau (Set-like) ; sinon un simple string (empty).
                    current={h.filterKey ? (colFilters[h.filterKey] ?? (h.multi ? [] : "")) : (h.multi ? [] : "")}
                    onSort={(dir) => doSort(h.key, dir)}
                    onFilter={h.filterKey ? (v) => doFilter(h.filterKey, v) : () => {}}
                    sortActive={sortKey === h.key}
                    sortDir={sortDir}
                    testId={h.testId}
                    multi={h.multi}
                    // Spec rev102 — bloc secondaire optionnel (utilisé sur
                    // Assigné(s) pour cumuler filtre utilisateurs + état).
                    secondaryLabel={h.secondaryLabel}
                    secondaryOptions={h.secondaryOptions}
                    secondaryCurrent={h.secondaryFilterKey ? (colFilters[h.secondaryFilterKey] ?? []) : undefined}
                    onSecondaryFilter={h.secondaryFilterKey ? (v) => doFilter(h.secondaryFilterKey, v) : undefined}
                  />
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((t) => {
              // Spec 2026-02-XX rev40 — Suppression de la coloration de fond
              // par statut. Le badge Statut coloré + la barre latérale gauche
              // (boxShadow inset) restent seuls porteurs de l'information. La
              // teinte de fond ajoutait du bruit visuel sur les tableaux denses
              // et redondait l'info du badge. Cohérent avec Documents rev17.
              const author = usersById[t.created_by];
              const attCount = t.attachments_count ?? 0;
              const msgCount = t.messages_count ?? 0;
              const chkTotal = (t.checklist || []).length;
              const chkDone = (t.checklist || []).filter((c) => c.done).length;
              return (
                <TableRow
                  key={t.id}
                  className="cursor-pointer align-top transition-colors hover:bg-white/[0.03]"
                  onClick={() => navigate(`/taches/${t.id}`)}
                  style={{
                    boxShadow: `inset 4px 0 0 0 ${STATUS_COLOR[t.effective_status] || "#333"}`,
                  }}
                  data-testid={`task-row-${t.id}`}
                  title={`Statut : ${STATUS_LABEL[t.effective_status] || t.effective_status}`}
                >
                  {/* --- Identification ------------------------------- */}
                  <TableCell>
                    {/*
                      Spec 2026-02-XX rev35 — Bloc 2 lignes :
                        L1 : référence tâche (monospace jaune 12px)
                        L2 : badge document lié (icône Link2 10px + réf 9px)
                             cliquable, ne s'affiche que si linked_document_id.
                    */}
                    <div className="flex items-center justify-between gap-2 min-w-0 task-ref-wrap">
                    <div className="flex flex-col items-start gap-0.5 min-w-0">
                      <span
                        className="font-bold tabular-nums"
                        style={{
                          fontFamily: '"SF Mono","Consolas","Menlo",monospace',
                          fontSize: 12, color: "#F9BC26", letterSpacing: "0.01em",
                          lineHeight: 1.1,
                        }}
                        data-testid={`row-ref-${t.id}`}
                      >
                        {t.reference || "—"}
                      </span>
                      {t.linked_document_id && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); navigate(`/documents/${t.linked_document_id}`); }}
                          className="inline-flex items-center gap-1 hover:brightness-125 transition-all max-w-full"
                          style={{
                            background: "transparent",
                            border: 0,
                            padding: 0,
                            color: "#5A9EE0",
                            fontFamily: '"SF Mono","Consolas","Menlo",monospace',
                            fontSize: 9,
                            letterSpacing: "0.02em",
                            lineHeight: 1,
                            cursor: "pointer",
                          }}
                          title={`Document lié : ${t.linked_document_ref || t.linked_document_id}`}
                          data-testid={`task-linked-doc-${t.id}`}
                        >
                          <Link2 size={10} strokeWidth={2.2} className="shrink-0" />
                          <span className="truncate">{t.linked_document_ref || "DOC"}</span>
                        </button>
                      )}
                    </div>
                    {(t.recurrence || ((t.relances?.count || 0) > 0 && !["to_verify", "completed", "closed"].includes(t.effective_status))) && (
                      <div className="flex items-center shrink-0" style={{ gap: 6 }} data-testid={`row-ref-icons-${t.id}`}>
                        {t.recurrence && <span title="Tâche récurrente" className="inline-flex" data-testid={`row-recurring-${t.id}`}><Repeat size={18} style={{ color: "#3568B0" }} /></span>}
                        {/* iter403 pt 1 — la cloche est réservée aux relances ; iter426 pt 1 — masquée quand la tâche est À vérifier / Terminée / Clôturée (réapparaît après Refaire). */}
                        {(t.relances?.count || 0) > 0 && !["to_verify", "completed", "closed"].includes(t.effective_status) && <RelanceBell task={t} usersById={usersById} />}
                      </div>
                    )}
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">
                    {/*
                      Spec 2026-02-XX rev2 (Feb 2026) — titre systématiquement
                      tronqué sur UNE ligne (nowrap + ellipsis) pour garantir
                      des hauteurs de ligne uniformes. Tooltip natif expose
                      le titre complet au survol.
                    */}
                    <div
                      className="truncate"
                      title={t.indice ? `${t.subject || ""} : ${t.indice}` : (t.subject || "")}
                      data-testid={`row-subject-${t.id}`}
                    >
                      {t.subject}{t.indice && <><span className="font-normal" style={{ color: "#a4a4ae" }}> : </span><span style={{ color: "#f4c877", fontWeight: 500 }} data-testid={`row-indice-${t.id}`}>{t.indice}</span></>}
                    </div>
                  </TableCell>
                  {/*
                    Spec 2026-02-XX rev2 — colonne fusionnée "Statut · Prio".
                    Les 2 badges sont empilés verticalement (Statut au-dessus,
                    Priorité en dessous). Économise ~90px de largeur.
                  */}
                  <TableCell
                    data-testid={`row-status-${t.id}`}
                  >
                    <StatusBadge
                      status={t.relance_active && !["to_verify", "completed", "closed"].includes(t.effective_status) ? "relance" : t.effective_status}
                      title={t.relance_active && !["to_verify", "completed", "closed"].includes(t.effective_status) ? `Relancé · ${STATUS_LABEL[t.effective_status] || t.effective_status}` : undefined}
                      verifiers={null}
                      verifierUser={t.effective_status === "to_verify" ? usersById?.[t.created_by] : null}
                    />
                  </TableCell>
                  {/* Spec 2026-02-XX rev6 — Priorité RÉSÉPARÉE en col dédiée. */}
                  <TableCell
                    data-testid={`row-priority-${t.id}`}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PriorityQuickPicker task={t} onChanged={onPriorityChanged} />
                  </TableCell>

                  {/* --- Destination ---------------------------------- */}
                  <TableCell
                    className="text-sm truncate"
                    title={projectsById[t.project_id]?.name || ""}
                    data-testid={`task-row-project-${t.id}`}
                  >{projectsById[t.project_id]?.code_label || projectsById[t.project_id]?.name || "—"}</TableCell>

                  {/* --- Personnes ----------------------------------- */}
                  {/*
                    Spec 2026-02-XX rev2 — "Créé par" en avatar circle 28×28
                    (initiales + tooltip nom complet). Économise ~65px.
                  */}
                  <TableCell className="text-center" data-testid={`row-author-${t.id}`}>
                    {/*
                      Spec 2026-02-XX rev38 (Point 4) — CRÉÉ column épurée.
                      Avatar seul (24px) + tooltip natif « Prénom Nom · Créé le
                      JJ-MM-AA HH:MM » — cohérent avec la colonne équivalente
                      du module Documents. Retrait de l'AvatarNamePopover
                      (surchargeait la ligne, informations redondantes avec la
                      fiche tâche).
                    */}
                    {author ? (() => {
                      const fullName = `${author.first_name} ${author.last_name}`.trim();
                      const dt = new Date(t.created_at || Date.now());
                      const pad = (n) => String(n).padStart(2, "0");
                      const dateStr = `${pad(dt.getDate())}-${pad(dt.getMonth() + 1)}-${String(dt.getFullYear()).slice(2)} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
                      return (
                        <span className="inline-flex shrink-0 cursor-help" title={`${fullName} · Créé le ${dateStr}`}>
                          <UserAvatar user={author} size={24} showPopover={false} variant="initials" />
                        </span>
                      );
                    })() : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  {/*
                    Spec 2026-02-XX rev7 (Feb 2026) — fond avatar = couleur
                    UNIQUE du user (via ``avatarColorForSeed``) pour repérer
                    d'un coup d'œil qui est qui, invariant à travers tous les
                    modules. Le statut d'assignation (accepté / refusé / en
                    attente) est maintenant matérialisé par un petit dot 8×8
                    en bas-droite de l'avatar (ring 2px sur le fond sombre du
                    tableau pour rester lisible sur les avatars clairs).
                  */}
                  <TableCell className="text-sm" data-testid={`row-assignees-${t.id}`}>
                    <div className="flex items-center">
                      {(t.assignments || []).slice(0, 3).map((a, ai) => {
                        const u = usersById[a.user_id];
                        if (!u) return null;
                        const st = a.status || "waiting";
                        const stColor = STATUS_COLOR[st] || "#9CA3AF";
                        const roleLabel = a.role === "manager" ? "manager" : (a.role === "helper" ? "aide" : "exécuteur");
                        const fullName = `${u.first_name} ${u.last_name}`.trim();
                        return (
                          <AvatarNamePopover
                            key={a.id}
                            name={fullName}
                            subtitle={`${roleLabel} · ${STATUS_LABEL[st] || st}`}
                            testid={`row-assignee-popover-${t.id}-${a.user_id}`}
                          >
                            <span
                              className="relative inline-flex shrink-0 rounded-full"
                              style={{ marginLeft: ai === 0 ? 0 : -5, zIndex: 3 - ai }}
                              title={`${fullName} — ${roleLabel} · ${STATUS_LABEL[st] || st}`}
                              data-testid={`row-assignee-${t.id}-${a.user_id}`}
                            >
                              <UserAvatar user={u} size={24} showPopover={false} variant="initials" />
                              {/* Dot de statut : couleur du STATUS_COLOR, ring 2px sombre. */}
                              <span
                                aria-hidden="true"
                                style={{
                                  position: "absolute",
                                  bottom: -1,
                                  right: -1,
                                  width: 9,
                                  height: 9,
                                  borderRadius: 999,
                                  background: stColor,
                                  boxShadow: "0 0 0 2px #1B1B1B",
                                }}
                              />
                            </span>
                          </AvatarNamePopover>
                        );
                      })}
                      {(t.assignments || []).length > 3 && (
                        <span
                          className="text-[11px] text-muted-foreground ml-2"
                          title={`${(t.assignments || []).length - 3} assigné(s) supplémentaire(s)`}
                        >
                          +{(t.assignments || []).length - 3}
                        </span>
                      )}
                    </div>
                  </TableCell>

                  {/* --- Lecture (spec 2026-08-02 point #6) --- */}
                  <TableCell
                    className="text-sm whitespace-nowrap"
                    data-testid={`row-read-${t.id}`}
                  >
                    <ReadStatusBadge task={t} usersById={usersById} />
                  </TableCell>

                  {/* --- Planification : Deadline (spec rev156 · variante compacte) --- */}
                  <TableCell
                    className="text-sm whitespace-nowrap"
                    data-testid={`row-deadline-${t.id}`}
                  >
                    <DeadlineCellCompact iso={t.deadline} closedAt={t.completed_at} doneAt={t.done_at} />
                  </TableCell>

                  {/* --- Pièces jointes (spec rev65 · retour translucide jaune #F9BC26) --- */}
                  <TableCell data-testid={`row-attachments-${t.id}`}>
                    <span
                      className="inline-flex items-center justify-center rounded-full cursor-help relative"
                      style={{
                        width: 26, height: 26,
                        background: attCount > 0 ? "rgba(249,188,38,0.14)" : "rgba(107,114,128,0.10)",
                        color: attCount > 0 ? "#F9BC26" : "#6B7280",
                        border: "1px solid " + (attCount > 0 ? "rgba(249,188,38,0.35)" : "rgba(107,114,128,0.25)"),
                      }}
                      title={attCount > 0 ? `${attCount} pièce(s) jointe(s)` : "Aucune pièce jointe"}
                      data-testid={`row-att-${t.id}`}
                    >
                      {/* Spec iter127 pt.2 (Feb 2026) — icône alignée sur
                          l'icône Lecture (26 px pill, 15 px icône). */}
                      <Paperclip size={15} strokeWidth={2.4} />
                      {attCount > 0 && (
                        <span
                          className="absolute -top-1 -right-1 min-w-[18px] h-[15px] px-1 rounded-full text-[9.5px] font-bold flex items-center justify-center leading-none tabular-nums"
                          style={{ background: "#1A1A1A", color: "#F9BC26", border: "1px solid rgba(249,188,38,0.55)" }}
                        >{attCount}</span>
                      )}
                    </span>
                  </TableCell>

                  {/* --- Checklist + métadonnées (rev178 · tooltip enrichi HoverCard) --- */}
                  <TableCell data-testid={`row-checklist-${t.id}`}>
                    <div className="flex items-center gap-1.5 whitespace-nowrap">
                      <HoverCard openDelay={150} closeDelay={80}>
                        <HoverCardTrigger asChild>
                          <span
                            className="inline-flex items-center justify-center rounded-full cursor-help relative"
                            style={{
                              width: 26, height: 26,
                              background: chkTotal > 0 ? "rgba(249,188,38,0.14)" : "rgba(107,114,128,0.10)",
                              color: chkTotal > 0 ? "#F9BC26" : "#6B7280",
                              border: "1px solid " + (chkTotal > 0 ? "rgba(249,188,38,0.35)" : "rgba(107,114,128,0.25)"),
                            }}
                            data-testid={`row-chk-${t.id}`}
                          >
                            {/* Spec iter127 pt.2 (Feb 2026) — icône alignée
                                sur l'icône Lecture (26 px pill, 15 px icône). */}
                            <ListChecks size={15} strokeWidth={2.4} />
                            {chkTotal > 0 && (
                              <span
                                className="absolute -top-1 -right-1 min-w-[28px] h-[15px] px-1 rounded-full text-[9.5px] font-bold flex items-center justify-center leading-none tabular-nums"
                                style={{ background: "#1A1A1A", color: "#F9BC26", border: "1px solid rgba(249,188,38,0.55)" }}
                              >{chkDone}/{chkTotal}</span>
                            )}
                          </span>
                        </HoverCardTrigger>
                        <HoverCardContent
                          side="right"
                          align="start"
                          sideOffset={8}
                          className="p-0"
                          style={{ width: 230, background: "#1A1A1E", border: "1px solid #2E2E33", borderRadius: 10 }}
                          data-testid={`row-chk-tooltip-${t.id}`}
                        >
                          {chkTotal === 0 ? (
                            <div className="p-3 text-[11px] text-muted-foreground">Aucune checklist</div>
                          ) : (
                            <div className="p-3">
                              {/* Header */}
                              <div className="flex items-center gap-2 mb-2">
                                <ListChecks size={13} style={{ color: "#F9BC26" }} />
                                <span className="text-[12px] font-semibold">
                                  Checklist ·{" "}
                                  <span className="tabular-nums" style={{ color: "#F9BC26" }}>{chkDone}/{chkTotal}</span>
                                </span>
                              </div>
                              {/* Progress bar */}
                              <div style={{ background: "#2E2E33", height: 4, borderRadius: 2, marginBottom: 10 }}>
                                <div style={{
                                  background: "#F9BC26",
                                  height: 4, borderRadius: 2,
                                  width: `${Math.round((chkDone / chkTotal) * 100)}%`,
                                  transition: "width 200ms ease",
                                }} />
                              </div>
                              {/* Items */}
                              <ul className="space-y-1.5 max-h-[280px] overflow-y-auto">
                                {(t.checklist || []).map((it) => (
                                  <li key={it.id} className="flex items-start gap-2 text-[11.5px] leading-tight">
                                    {it.done ? (
                                      <>
                                        <CheckSquare size={13} strokeWidth={2.4} style={{ color: "#5AB87A", marginTop: 1, flexShrink: 0 }} />
                                        <span className="line-through" style={{ color: "#6B6B70" }}>{it.label}</span>
                                      </>
                                    ) : (
                                      <>
                                        <span
                                          className="rounded-[3px] flex-shrink-0"
                                          style={{ width: 13, height: 13, marginTop: 1, border: "1.5px solid #4A4A4F" }}
                                        />
                                        <span style={{ color: "#C8C8CC" }}>{it.label}</span>
                                      </>
                                    )}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </HoverCardContent>
                      </HoverCard>
                    </div>
                  </TableCell>
                  {/* --- Discussion (col DÉDIÉE — spec 2026-02-XX rev6) --------
                       Bulle SVG lucide-style + badge rouge overlay non-lus.
                       Spec 2026-02-XX rev35 — le badge « document lié » a
                       été déplacé vers la colonne Réf (ligne 2). Cette
                       cellule reste strictement neutre tant qu'aucun
                       message n'a été échangé. */}
                  <TableCell className="text-center" data-testid={`row-chat-${t.id}`}>
                    <DiscussionCell
                      taskId={t.id}
                      total={msgCount}
                      unread={t.unread_messages_count ?? 0}
                      isSolo={!(user?.role === "admin" || user?.is_first_admin) && (t.assignments || []).every((a) => a.user_id === t.created_by)}
                    />
                  </TableCell>

                  {/* --- Actions (spec 2026-02-XX rev48) ---
                       Trois avatars ronds 22 px, couleurs sémantiques
                       universelles (Info bleu / Édition gris / Suppr. rouge)
                       harmonisés avec le module Documents. Cf. `hasOwnership`
                       & `isAdmin` : la suppression reste réservée aux admins,
                       les autres icônes sont accessibles à tous. Le clic
                       déclenche la navigation vers la fiche tâche pour
                       maintenir un flow uniforme (les actions se font sur la
                       fiche détail). */}
                  {/* Spec iter278 (2026-09) — Cellule « Activité » en pastille
                      compacte, identique au module Documents (iter264) :
                      < 1 h vert (#1a2a1e/#5dcaa5), < 24 h neutre (#c8c8cc),
                      au-delà neutre atténué (#8a8a8f). Format sans « il y a ». */}
                  {sortKey === "recent_activity" && (
                    <TableCell className="whitespace-nowrap text-center overflow-hidden" style={{ minWidth: 84 }} data-testid={`row-activity-${t.id}`}>
                      {(() => {
                        const iso = t.activity?.at || t.updated_at || t.created_at;
                        if (!iso) return (
                          <span className="italic text-[10.5px]" style={{ color: "rgba(255,255,255,0.25)" }}>—</span>
                        );
                        return (
                          <TaskActivityHovercard task={t} user={usersById?.[t.activity?.actor_id]} usersById={usersById}>
                            <span className="inline-flex items-center tabular-nums" style={activityPillStyle(iso)}>
                              {formatActivityShort(iso)}
                            </span>
                          </TaskActivityHovercard>
                        );
                      })()}
                    </TableCell>
                  )}
                  <TableCell className="text-center whitespace-nowrap" data-testid={`row-actions-${t.id}`}
                    onClick={(e) => e.stopPropagation()}>
                    <div className="inline-flex items-center gap-1 justify-center">
                      <HoverCard openDelay={150} closeDelay={80}>
                        <HoverCardTrigger asChild>
                          <button
                            type="button"
                            onClick={() => navigate(`/taches/${t.id}`)}
                            className="inline-flex items-center justify-center transition-all hover:scale-[1.08] hover:saturate-[1.15]"
                            style={{
                              width: 22, height: 22, borderRadius: 999,
                              background: "rgba(90,158,224,0.14)",
                              border: "1px solid rgba(90,158,224,0.38)",
                              color: "#5A9EE0",
                            }}
                            title="Voir la fiche tâche"
                            data-testid={`task-info-${t.id}`}
                          >
                            <Info size={11} strokeWidth={2.4} />
                          </button>
                        </HoverCardTrigger>
                        <HoverCardContent
                          side="left"
                          align="end"
                          sideOffset={8}
                          collisionPadding={12}
                          sticky="always"
                          className="p-0 border-0 shadow-2xl"
                          style={{ width: 460, background: "transparent" }}
                          data-testid={`task-info-popover-${t.id}`}
                        >
                          <TaskDetailsPreview
                            task={t}
                            projectsById={projectsById}
                            usersById={usersById}
                          />
                        </HoverCardContent>
                      </HoverCard>
                      <button
                        type="button"
                        onClick={() => navigate(`/taches/${t.id}?edit=1`)}
                        className="inline-flex items-center justify-center transition-all hover:scale-[1.08] hover:saturate-[1.15]"
                        style={{
                          width: 22, height: 22, borderRadius: 999,
                          background: "#262931",
                          border: "1px solid rgba(156,163,175,0.28)",
                          color: "#9CA3AF",
                        }}
                        title="Modifier"
                        data-testid={`task-edit-${t.id}`}
                      >
                        <Pencil size={11} strokeWidth={2.4} />
                      </button>
                      <button
                        type="button"
                        disabled={!(t.created_by === user?.id || !!user?.is_first_admin)}
                        onClick={() => navigate(`/taches/${t.id}?delete=1`)}
                        className="inline-flex items-center justify-center transition-all hover:scale-[1.08] hover:saturate-[1.15] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
                        style={{
                          width: 22, height: 22, borderRadius: 999,
                          background: "rgba(239,68,68,0.14)",
                          border: "1px solid rgba(239,68,68,0.38)",
                          color: "#EF4444",
                        }}
                        title={t.created_by === user?.id || !!user?.is_first_admin ? "Supprimer" : "Seul le créateur de la tâche peut la supprimer"}
                        data-testid={`task-delete-${t.id}`}
                      >
                        <Trash2 size={11} strokeWidth={2.4} />
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={HEADERS_WITH_ACTIVITY.length} className="p-0">
                  {/* Spec iter130 (Feb 2026) — EmptyState unifié. */}
                  <EmptyState
                    icon={<ListChecks size={44} strokeWidth={1.5} />}
                    title="Aucune tâche"
                    description="Créez une nouvelle tâche ou retirez un filtre actif pour voir les tâches existantes."
                    ctaLabel={onCreateEmpty ? "+ Créer une tâche" : null}
                    onCta={onCreateEmpty || null}
                    color={"#F97316"}
                    testId="tasks-empty-state"
                  />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </table>
      </div>

      {/*
        Spec 2026-02-XX rev2 (Feb 2026) — Légende visible de la barre de
        statut verticale à gauche de chaque ligne. La barre est appliquée
        via boxShadow inset sur le TableRow, ce qui rendait sa signification
        invisible. Cette légende compacte mappe les 8 statuts effectifs à
        leurs couleurs pour lever toute ambiguïté d'un coup d'œil.
      */}
      <div
        className="flex items-center gap-4 flex-wrap px-2 -mt-1 text-[11px] text-muted-foreground"
        data-testid="tasks-status-legend"
      >
        <span className="text-[11px] text-foreground/80 font-semibold">Barre de statut :</span>
        {["planned","waiting","accepted","in_progress","late","relance","to_verify","redo","refused","closed"].map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block rounded-sm"
              style={{ width: 3, height: 12, background: STATUS_COLOR[s] || "#333" }}
            />
            {STATUS_LABEL[s] || s}
          </span>
        ))}
      </div>

      {hasMore && (
        <div
          ref={sentinelRef}
          className="w-full text-center py-4 text-xs text-muted-foreground"
          data-testid="tasks-load-more-sentinel"
        >
          {loadingMore ? (
            <span>Chargement…</span>
          ) : (
            <button
              type="button"
              onClick={onLoadMore}
              className="underline hover:text-white transition-colors"
              data-testid="tasks-load-more-btn"
            >
              Charger plus ({items.length} sur {total})
            </button>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
// ChecklistDots — spec 2026-02-XX rev10 (Feb 2026) — miroir des progress
// dots Documents pour le module Tâches. Un point 13×13 (rounded 3px) par
// item de la checklist. Cinq états couleur :
//   • gris    #3A3A3A → item pas encore fait, tâche sans deadline / OK
//   • orange  #F97316 → item en cours, tâche dans les délais
//   • rouge   #EF4444 → item en cours, tâche EN RETARD (effective_status=late)
//   • vert    #22C55E → item fait, tâche en cours
//   • jaune   #EAB308 → item fait, tâche complétée / clôturée
// Tooltip natif au survol (via <title>) — libellé de l'item + statut.
// ---------------------------------------------------------------------------
function ChecklistDots({ task }) {
  const items = task.checklist || [];
  if (items.length === 0) return null;
  const isTaskLate = task.effective_status === "late";
  // Spec 2026-02-XX rev17 (point 7 corrigé — cohérence Documents/Tâches) :
  // système à 4 ÉTATS uniquement.
  //   • GRIS   #3A3A3A — item pas encore fait
  //   • VERT   #22C55E — item fait
  //   • ROUGE  #EF4444 — item pas encore fait + tâche EN RETARD
  //   • JAUNE  #EAB308 — tâche entière CLÔTURÉE → tous les points au
  //                     jaune simultanément, indépendamment de leur
  //                     état individuel précédent.
  // L'orange (« à faire ») est SUPPRIMÉ.
  const isTaskClotured = task.status === "closed" || task.effective_status === "closed";

  const stateFor = (item) => {
    if (isTaskClotured) return { color: "#EAB308", label: "Tâche clôturée" };
    if (item.done)      return { color: "#22C55E", label: "Fait" };
    if (isTaskLate)     return { color: "#EF4444", label: "À faire · en retard" };
    return { color: "#3A3A3A", label: "À faire" };
  };

  const tooltip = items
    .map((it, i) => {
      const st = stateFor(it);
      return `${String(i + 1).padStart(2, "0")}. ${it.label || "Item"} — ${st.label}`;
    })
    .join("\n");

  return (
    <span className="inline-flex gap-1.5 items-center" title={tooltip}>
      {items.map((it, i) => {
        const st = stateFor(it);
        return (
          <span
            key={it.id || i}
            title={`${it.label || "Item"} — ${st.label}`}
            style={{
              width: 13, height: 13, borderRadius: 3,
              background: st.color,
              display: "inline-block",
              border: st.color === "#3A3A3A" ? "1px solid rgba(255,255,255,0.05)" : "none",
            }}
          />
        );
      })}
    </span>
  );
}


// ---------------------------------------------------------------------------
// DiscussionCell — dedicated cell for the extreme-right "Discussion" column.
// Spec 2026-02-16 :
//   • 30×30 circle, bg rgba(59,156,224,0.15), thin-stroke bubble icon #3B9CE0
//   • Red badge top-right (min 14 px, #EF4444) with unread count OR total
//     when there are no unread messages. Hidden entirely when total === 0.
//   • Click opens the task detail directly scrolled to the Discussion
//     section (URL query ?scroll=chat picked up by TaskDetail.jsx). The
//     click stops event propagation so the row's own default navigate
//     (opens the task summary) doesn't fire at the same time.
// ---------------------------------------------------------------------------
// Spec 2026-02-XX rev42 (Point 3 · clarification) — L'icône de discussion
// est GRISÉE lorsque la tâche est « solo » : tous les assignés sont la
// même personne que le créateur. Il n'y a alors aucun autre interlocuteur
// possible, donc pas de discussion. Le tooltip natif l'explicite au survol.
//
// Dans tous les autres cas (au moins un exécuteur ≠ créateur), l'icône est
// bleue et cliquable → ouvre l'onglet Discussion de la fiche tâche.
// ---------------------------------------------------------------------------
function DiscussionCell({ taskId, total, unread, isSolo = false }) {
  const navigate = useNavigate();
  const hasUnread = (unread || 0) > 0;
  const overNine = (unread || 0) > 9;

  const go = (e) => {
    e.stopPropagation();
    if (isSolo) return;
    navigate(`/taches/${taskId}?scroll=chat`);
  };

  return (
    <button
      type="button"
      onClick={go}
      disabled={isSolo}
      title={isSolo
        ? "Tâche solo (créateur = seul assigné) — aucun autre interlocuteur, discussion indisponible"
        : hasUnread
          ? `${unread} message${unread > 1 ? "s" : ""} non lu${unread > 1 ? "s" : ""}`
          : (total > 0
              ? `${total} message${total > 1 ? "s" : ""} — tous lus`
              : "Aucun message — ouvrir la discussion")
      }
      className={`relative inline-flex items-center justify-center transition-transform ${isSolo ? "" : "hover:scale-105"}`}
      style={{
        width: 26, height: 26,
        borderRadius: 999,
        // Spec rev65 (2026-08-02) — retour au vert WhatsApp translucide
        // (#25D366 sur rgba(37,211,102,0.14)) pour un rendu plus doux et
        // cohérent avec Pièces jointes / Checklist / Lecture. État solo
        // (créateur = seul assigné) conserve son cadre gris translucide.
        background: isSolo ? "rgba(107,114,128,0.10)" : "rgba(37,211,102,0.14)",
        color: isSolo ? "#6B7280" : "#25D366",
        border: `1px solid ${isSolo ? "rgba(107,114,128,0.25)" : "rgba(37,211,102,0.35)"}`,
        cursor: isSolo ? "not-allowed" : "pointer",
      }}
      data-testid={`discussion-btn-${taskId}`}
    >
      <MessageCircle size={13} strokeWidth={2.6} />
      {hasUnread && !isSolo && (
        <span
          className="absolute font-bold tabular-nums text-white flex items-center justify-center leading-none"
          style={{
            top: -4,
            right: -4,
            minWidth: 14,
            height: 14,
            padding: overNine ? "0 3px" : 0,
            fontSize: 8,
            background: "#EF4444",
            borderRadius: 999,
            boxShadow: "0 0 0 2px #1E1E1E",
          }}
          data-testid={`discussion-badge-${taskId}`}
        >
          {overNine ? "9+" : unread}
        </span>
      )}
    </button>
  );
}
